/**
 * API Context
 *
 * HTTP client wrapper for communicating with the Humanizer API.
 * Automatically includes Bearer token when authenticated.
 */

import React, { createContext, useContext, useMemo } from 'react';
import ky, { type KyInstance } from 'ky';
import { useAuth } from './AuthContext';
import type { DetectionResult, SicResult, SentenceSessionInfo, SentenceStepResult } from '../types/analysis';
import type { TransformResult, HumanizationResult, PresetInfo, PersonaProfileInput } from '../types/transform';
import type { ApiMediaItem } from '../types/media';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SessionSummary {
  id: string;
  name?: string;
  userId?: string;
  createdAt: string;
  updatedAt: string;
  bufferCount: number;
}

export interface Session {
  id: string;
  name?: string;
  userId?: string;
  createdAt: string | number;
  updatedAt: string | number;
  buffers?: Array<{ name: string; version?: string; branch?: string }>;
  searchContext?: {
    query?: string;
    resultsCount: number;
    anchorsCount: number;
  };
}

export type BufferType = 'working' | 'voice' | 'reference' | 'arc';

export interface BufferSummary {
  name: string;
  version: string;
  bufferType: BufferType;
  currentBranch: string;
  tags: string[];
  description?: string;
  createdAt: string;
  updatedAt: string;
  contentLength: number;
}

export interface Buffer {
  name: string;
  version: string;
  currentBranch: string;
  content: unknown[];
  workingContent: unknown[];
  isDirty: boolean;
  branches: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Result provenance - tracks where content came from
 */
export interface ResultProvenance {
  sourceStore: 'archive' | 'books';
  sourceType: string;
  sourceOriginalId?: string;
  threadRootId?: string;
  threadTitle?: string;
  parentNodeId?: string;
  bookContext?: {
    bookId: string;
    bookTitle?: string;
    bookSlug?: string;
    chapterId?: string;
    chapterTitle?: string;
    chapterPosition?: number;
  };
  sourceCreatedAt?: number;
  author?: string;
  authorRole?: 'user' | 'assistant' | 'system';
  uri: string;
}

/**
 * Quality indicators for a result
 */
export interface QualityIndicators {
  qualityScore: number;
  wordCount: number;
  hasCodeBlocks?: boolean;
  hasMathBlocks?: boolean;
  hasLinks?: boolean;
  hasCitations?: boolean;
  languageScore?: number;
}

/**
 * AI-generated enrichment for a result
 */
export interface ResultEnrichment {
  title?: string;
  summary?: string;
  rating?: number;
  categories?: string[];
  keyTerms?: string[];
  enrichedAt?: number;
}

/**
 * Score breakdown for a search result
 */
export interface ScoreBreakdown {
  denseScore?: number;
  denseRank?: number;
  sparseScore?: number;
  sparseRank?: number;
  fusedScore: number;
  anchorBoost?: number;
  finalScore: number;
}

/**
 * Search result from the API with full provenance and quality data
 */
export interface SearchResult {
  id: string;
  text: string;
  source: 'archive' | 'books';
  score: number;
  wordCount: number;
  hierarchyLevel: number;
  provenance?: ResultProvenance;
  quality?: QualityIndicators;
  enrichment?: ResultEnrichment;
  scoreBreakdown?: ScoreBreakdown;
  title?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface ClusterSummary {
  id: string;
  label: string;
  description?: string;
  nodeCount: number;
  quality: number;
  createdAt: string;
  updatedAt: string;
}

// Archive types
export interface ArchiveNode {
  id: string;
  uri: string;
  text: string;
  fullText?: string;
  title?: string;
  sourceType: string;
  sourceAdapter?: string;
  author?: string;
  authorRole?: 'user' | 'assistant' | 'system';
  parentNodeId?: string;
  threadRootId?: string;
  hierarchyLevel: number;
  wordCount: number;
  tags?: string[];
  mediaRefs?: Array<{
    id: string;
    type: string;
    mimeType?: string;
    localPath?: string;
  }>;
  sourceCreatedAt?: number;
  sourceTitle?: string;
  createdAt: number;
}

export interface ArchiveSource {
  sourceType: string;
  count: number;
  rawTypes?: string[]; // Grouped source types (e.g. ['facebook-conversation', 'facebook-message'])
}

export interface ArchiveThread {
  id: string;
  title: string;
  textPreview?: string;
  sourceType: string;
  author?: string;
  wordCount: number;
  sourceCreatedAt?: number;
  createdAt: number;
  excellenceScore?: number;
  excellenceTier?: string;
}

export interface ArchiveStats {
  totalNodes: number;
  nodesBySourceType: Record<string, number>;
  nodesByAdapter: Record<string, number>;
  nodesWithEmbeddings: number;
  totalLinks: number;
  totalJobs: number;
}

export interface BookSummary {
  id: string;
  title: string;
  description?: string;
  chapterCount: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// Admin types
export interface AdminStatus {
  status: string;
  message?: string;
}

export interface AdminUsageStats {
  totalTokens: number;
  totalRequests: number;
  totalCostCents: number;
  userCount: number;
  byTier?: Array<{ name: string; requests?: number; tokens?: number; cost?: number }>;
  daily?: Array<{ date: string; requests?: number; tokens?: number; users?: number }>;
  topUsers?: Array<{ userId: string; email?: string; requests?: number; tokens?: number }>;
}

export interface AdminApiKey {
  id: string;
  userId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  usageCount: number;
  createdAt: string;
  revokedAt: string | null;
}

export interface AdminTier {
  tier: string;
  tokensPerMonth: number;
  requestsPerMonth: number;
  costCentsPerMonth: number;
  requestsPerMinute: number;
  maxApiKeys: number;
  // Extended fields from API
  name?: string;
  description?: string;
  features?: string[];
  priceMonthly?: number;
  priceAnnual?: number;
  priority?: number;
  isPublic?: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  role: string;
  tenantId: string;
  createdAt: string;
  lastActiveAt?: string | null;
  bannedAt?: string | null;
  banReason?: string | null;
  usage?: {
    tokensUsed: number;
    requestsCount: number;
    costMillicents: number;
    period: string;
  } | null;
}

export interface AdminUserListParams {
  search?: string;
  tier?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface AdminPrompt {
  id: string;
  name: string;
  description?: string;
  template: string;
  version?: number;
  usedBy?: string[];
  tags?: string[];
  requiredVariables?: string[];
}

export interface AdminPromptUpdateParams {
  name?: string;
  description?: string;
  template: string;
  usedBy?: string[];
  tags?: string[];
  requiredVariables?: string[];
}

export interface AdminModel {
  id: string;
  name: string;
  provider: string;
  type: string;
  capabilities?: string[];
  contextWindow?: number;
  dimensions?: number;
  maxOutput?: number;
  costPerMtokInput?: number;
  costPerMtokOutput?: number;
  isDefault?: boolean;
  enabled: boolean;
}

export interface AdminProvider {
  id: string;
  name: string;
  type: 'local' | 'cloud';
  status: 'connected' | 'disconnected' | 'error' | 'healthy' | 'unhealthy' | 'unknown' | 'degraded';
  enabled: boolean;
  endpoint?: string;
  apiKeyConfigured: boolean;
  apiKeyHint?: string;
  models: Array<{ id: string; name: string; type: string; enabled: boolean }>;
  healthStatus?: 'healthy' | 'unhealthy' | 'degraded' | 'unknown';
  lastHealthCheck?: string;
  healthError?: string;
  errorMessage?: string;
  rateLimitRpm?: number;
  costPerMtokInput?: number;
  costPerMtokOutput?: number;
  priority?: number;
}

export interface AdminApiKeyValidationResult {
  valid: boolean;
  error?: string;
  errorCode?: string;
  type: 'format' | 'api' | 'success';
  availableModels?: string[];
  timestamp: string;
}

export interface AdminOllamaDiscoveryResult {
  success: boolean;
  modelsFound: number;
  modelsRegistered: number;
  models: Array<{
    id: string;
    name: string;
    capabilities: string[];
    dimensions?: number;
    contextWindow?: number;
    parameterSize?: string;
    quantization?: string;
    family?: string;
    isKnown: boolean;
  }>;
  error?: string;
  timestamp: string;
}

export interface AdminFeature {
  id: string;
  name: string;
  description: string;
  category: 'core' | 'premium' | 'beta' | 'experimental';
  enabled: boolean;
  tierOverrides: Array<{ tier: string; enabled: boolean }>;
  rolloutPercentage?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSubscription {
  id: string;
  userId: string;
  userEmail: string;
  tier: string;
  status: 'active' | 'past_due' | 'canceled' | 'trialing';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId: string;
  monthlyAmount: number;
  createdAt: string;
}

export interface AdminSubscriptionListParams {
  tier?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface AdminAuditEvent {
  id: string;
  timestamp: string;
  action: string;
  category: 'auth' | 'admin' | 'billing' | 'api' | 'system';
  actor: {
    type: 'user' | 'system' | 'api_key';
    id: string;
    email?: string;
  };
  target?: {
    type: string;
    id: string;
    name?: string;
  };
  metadata: Record<string, unknown>;
  ip?: string;
  userAgent?: string;
  success: boolean;
}

export interface AdminAuditListParams {
  category?: string;
  success?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AdminAgentStatus {
  id: string;
  name: string;
  house: string;
  status: 'idle' | 'working' | 'waiting' | 'error' | 'disabled';
  capabilities: string[];
  health: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    lastActive: number;
    tasksCompleted: number;
    tasksFailed: number;
    errorMessage?: string;
  };
}

export interface AdminCostAnalytics {
  period: { start: string; end: string };
  totalProviderCostMillicents: number;
  totalChargedMillicents: number;
  marginMillicents: number;
  marginPercent: number;
  byProvider: Record<string, { cost: number; charged: number; requests: number; tokens: number }>;
  byModel: Record<string, { cost: number; requests: number; tokens: number }>;
  daily: Array<{ date: string; cost: number; charged: number }>;
  recentTransactions?: Array<{
    createdAt: string;
    provider: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    providerCostMillicents: number;
    userChargeMillicents: number;
  }>;
}

// Settings types (user-facing)
export interface SettingsApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  usageCount: number;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface SettingsUsageSummary {
  period: string;
  usage: {
    tokensUsed: number;
    requestsCount: number;
    costMillicents: number;
  };
  limits: {
    tokensLimit: number;
    requestsLimit: number;
    costLimitMillicents: number;
  };
  tier: string;
  percentUsed: number;
  withinLimits: boolean;
  byModel: Record<string, number>;
  byOperation: Record<string, number>;
}

export interface SettingsUsageHistory {
  period: string;
  tokens: number;
  requests: number;
  costMillicents: number;
}

export interface SettingsPreferences {
  modelPreferences: {
    defaultModel?: string;
    temperature?: number;
    maxTokens?: number;
  };
  transformationDefaults: {
    persona?: string;
    style?: string;
    verbosity?: string;
    formality?: string;
  };
  uiPreferences: {
    theme?: 'light' | 'dark' | 'system';
    compactMode?: boolean;
    showTokenCount?: boolean;
  };
}

export interface SettingsCustomPrompt {
  id: string;
  name: string;
  description?: string;
  template: string;
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ContentOverride {
  id: string;
  nodeId: string;
  originalText?: string;
  editedText: string;
  editReason?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response from the AUI chat endpoint
 */
export interface AuiChatResponse {
  type: 'success' | 'error' | 'awaiting_input' | 'partial';
  message: string;
  data?: unknown;
  suggestions?: string[];
  awaitInput?: boolean;
  inputPrompt?: string;
  tokensUsed?: number;
  costCents?: number;
}

export interface ApiClient {
  // Sessions
  listSessions: () => Promise<{ sessions: SessionSummary[]; count: number }>;
  createSession: (options?: { userId?: string; name?: string }) => Promise<Session>;
  getSession: (id: string) => Promise<Session>;
  deleteSession: (id: string) => Promise<{ deleted: boolean }>;

  // Chat (LLM agent)
  chat: (
    sessionId: string,
    message: string,
    history?: Array<{ role: string; content: string }>
  ) => Promise<AuiChatResponse>;

  // Buffers
  listBuffers: (sessionId: string) => Promise<{ buffers: BufferSummary[]; count: number }>;
  createBuffer: (sessionId: string, name: string, content?: unknown[]) => Promise<BufferSummary>;
  getBuffer: (sessionId: string, name: string) => Promise<Buffer>;
  setBufferContent: (sessionId: string, name: string, content: unknown[]) => Promise<void>;
  appendToBuffer: (sessionId: string, name: string, items: unknown[]) => Promise<void>;
  commitBuffer: (sessionId: string, name: string, message: string) => Promise<{ version: string }>;
  getBufferHistory: (sessionId: string, name: string, limit?: number) => Promise<{ history: unknown[] }>;
  deleteBuffer: (sessionId: string, name: string, force?: boolean) => Promise<{ deleted: boolean }>;
  branchBuffer: (sessionId: string, name: string, branchName: string) => Promise<{ name: string; headVersionId: string; createdAt: string; parentBranch?: string }>;
  switchBranch: (sessionId: string, name: string, branchName: string) => Promise<{ switched: boolean; branch: string }>;
  rollbackBuffer: (sessionId: string, name: string, steps?: number) => Promise<{ id: string; message: string; timestamp: number }>;
  pinBuffer: (sessionId: string, name: string, pinned: boolean) => Promise<{ id: string; pinned: boolean }>;

  // User-scoped Buffers
  userBuffers: {
    list: (type?: BufferType) => Promise<{ buffers: BufferSummary[]; count: number }>;
    create: (body: { name: string; type: BufferType; content?: unknown[]; tags?: string[]; description?: string }) => Promise<BufferSummary>;
    getVoice: () => Promise<Buffer>;
    updateContent: (name: string, content: unknown[]) => Promise<void>;
    listAll: (sessionId?: string) => Promise<{ buffers: Array<BufferSummary & { scope: 'session' | 'user' }> }>;
  };

  // Search
  search: (
    sessionId: string,
    query: string,
    options?: { sources?: string[]; limit?: number; threshold?: number }
  ) => Promise<{ results: SearchResult[]; count: number }>;
  findSimilar: (
    sessionId: string,
    text: string,
    options?: { limit?: number; threshold?: number }
  ) => Promise<{ results: SearchResult[]; count: number }>;
  addAnchor: (
    sessionId: string,
    resultId: string,
    type: 'positive' | 'negative'
  ) => Promise<{ id: string; type: string }>;

  // Archive (UCG browsing)
  archive: {
    browse: (options?: {
      sourceType?: string;
      threadRootId?: string;
      parentNodeId?: string;
      hierarchyLevel?: number;
      authorRole?: 'user' | 'assistant' | 'system';
      limit?: number;
      offset?: number;
      orderBy?: 'createdAt' | 'sourceCreatedAt' | 'importedAt' | 'wordCount';
      orderDir?: 'asc' | 'desc';
    }) => Promise<{ nodes: ArchiveNode[]; total: number; hasMore: boolean }>;
    getSources: () => Promise<{ sources: ArchiveSource[]; total: number }>;
    getThreads: (options?: { sourceType?: string; limit?: number; offset?: number; dateFrom?: string; dateTo?: string }) => Promise<{ threads: ArchiveThread[]; total: number; hasMore: boolean }>;
    getThread: (threadId: string) => Promise<{ threadId: string; nodes: ArchiveNode[]; count: number }>;
    getNode: (nodeId: string) => Promise<{ node: ArchiveNode }>;
    getStats: () => Promise<ArchiveStats>;
  };

  // Media
  media: {
    list: (options?: { type?: string; sourceType?: string; limit?: number; offset?: number }) =>
      Promise<{ items: ApiMediaItem[]; total: number; hasMore: boolean; limit: number; offset: number }>;
    byPointer: (pointer: string) =>
      Promise<{ found: boolean; media?: ApiMediaItem }>;
    search: (query: string, limit?: number) =>
      Promise<{ results: ApiMediaItem[]; total: number }>;
    upload: (file: File, options?: { sourceType?: string; caption?: string; nodeId?: string }) =>
      Promise<{ item: ApiMediaItem; deduplicated?: boolean }>;
    transcribe: (mediaId: string, type: string, archiveId?: string) =>
      Promise<{ id: string; versionId?: string; text: string; type: string; model: string; status: string; processingDurationMs?: number }>;
    getTranscriptions: (mediaId: string, archiveId?: string) =>
      Promise<{ versions: Array<{ id: string; versionNumber: number; type: string; status: string; text?: string; segments?: unknown[]; model: { id: string; provider: string; variant?: string }; isPreferred: boolean; completedAt?: string; quality?: { confidence?: number; language?: string } }> }>;
    setPreferredTranscription: (versionId: string) =>
      Promise<{ success: boolean; previousPreferredId?: string; newPreferredId: string }>;
  };

  // Overrides (user content edits)
  overrides: {
    save: (nodeId: string, originalText: string, editedText: string, editReason?: string) => Promise<{ override: ContentOverride }>;
    get: (nodeId: string) => Promise<{ override: ContentOverride | null }>;
    getBatch: (nodeIds: string[]) => Promise<{ overrides: Record<string, ContentOverride> }>;
    delete: (nodeId: string) => Promise<{ deleted: boolean }>;
  };

  // Clusters
  listClusters: (options?: { userId?: string; limit?: number }) => Promise<{ clusters: ClusterSummary[] }>;
  getCluster: (clusterId: string) => Promise<{
    id: string;
    label: string;
    description: string;
    passages: Array<{ id: string; text: string; distanceFromCentroid?: number }>;
    totalPassages: number;
    coherence: number;
    keywords: string[];
  }>;
  discoverClusters: (options?: {
    minClusterSize?: number;
    maxClusters?: number;
  }) => Promise<{ clusters: ClusterSummary[]; totalPassages?: number; assignedPassages?: number; noisePassages?: number; durationMs?: number }>;
  generateClusterDraft: (
    clusterId: string,
    opts?: { targetWordCount?: number; arcType?: string; topN?: number }
  ) => Promise<{ arc: any; draft: string; cullReport: any; wordCount: number; passageCount: number; clusterLabel: string }>;

  // Books
  listBooks: (options?: { userId?: string; limit?: number }) => Promise<{ books: BookSummary[] }>;
  createBookFromCluster: (
    clusterId: string,
    options?: { title?: string; personaId?: string }
  ) => Promise<BookSummary>;
  exportBook: (bookId: string, format?: 'markdown' | 'html' | 'json') => Promise<string>;

  // Admin
  admin: {
    // Status
    getStatus: () => Promise<AdminStatus>;

    // Users
    listUsers: (params?: AdminUserListParams) => Promise<{ users: AdminUser[]; total: number }>;
    getUser: (userId: string) => Promise<AdminUser>;
    createUser: (params: { email: string; password?: string; role?: string; displayName?: string }) => Promise<AdminUser>;
    updateUser: (userId: string, params: { email?: string; displayName?: string }) => Promise<AdminUser>;
    updateUserRole: (userId: string, role: string, reason: string) => Promise<void>;
    banUser: (userId: string, reason: string, duration?: string) => Promise<void>;
    unbanUser: (userId: string) => Promise<void>;
    deleteUser: (userId: string) => Promise<void>;

    // Tiers
    listTiers: () => Promise<{ tiers: AdminTier[] }>;
    updateTier: (tierId: string, params: Partial<AdminTier>) => Promise<void>;

    // Pricing
    listPricing: () => Promise<{ pricing: Array<{ key: string; label: string; current: number; default: number; isOverridden: boolean }> }>;
    updatePricing: (key: string, value: number) => Promise<void>;
    listProviderCosts: () => Promise<{ rates: Array<{ provider: string; modelId: string; inputCostPerMtok: number; outputCostPerMtok: number; effectiveFrom: string; effectiveUntil: string | null }>; total: number }>;

    // API Keys
    listApiKeys: (params?: { userId?: string; status?: string; limit?: number; offset?: number }) => Promise<{ keys: AdminApiKey[]; total: number }>;
    revokeApiKey: (keyId: string, reason?: string) => Promise<void>;

    // Models
    listModels: () => Promise<{ models: AdminModel[]; byProvider: Record<string, AdminModel[]>; total: number }>;
    updateModel: (modelId: string, params: { enabled?: boolean; isDefault?: boolean }) => Promise<void>;

    // Prompts
    listPrompts: () => Promise<{ prompts: AdminPrompt[] }>;
    getPrompt: (id: string) => Promise<AdminPrompt>;
    updatePrompt: (id: string, params: AdminPromptUpdateParams) => Promise<void>;

    // Providers
    listProviders: () => Promise<{ providers: AdminProvider[] }>;
    updateProvider: (providerId: string, params: { enabled?: boolean; endpoint?: string; apiKey?: string; priority?: number }) => Promise<void>;
    checkProviderHealth: (providerId: string) => Promise<{ status: string; latencyMs: number; error?: string; consecutiveFailures?: number; checkedAt: string }>;
    validateProviderKey: (providerId: string, apiKey: string) => Promise<AdminApiKeyValidationResult>;
    removeProviderApiKey: (providerId: string) => Promise<{ providerId: string; apiKeyRemoved: boolean }>;
    discoverOllamaModels: () => Promise<AdminOllamaDiscoveryResult>;

    // Features
    listFeatures: () => Promise<{ features: AdminFeature[] }>;
    updateFeature: (featureId: string, params: { enabled?: boolean; rolloutPercentage?: number; tierOverrides?: Array<{ tier: string; enabled: boolean }> }) => Promise<void>;

    // Subscriptions
    listSubscriptions: (params?: AdminSubscriptionListParams) => Promise<{ subscriptions: AdminSubscription[]; stats: { total: number; active: number; canceling: number; mrr: number }; total: number }>;
    getSubscription: (subscriptionId: string) => Promise<AdminSubscription>;

    // Analytics
    getUsage: (params?: { startDate?: string; endDate?: string; groupBy?: string }) => Promise<AdminUsageStats & { byModel?: Record<string, unknown>; byOperation?: Record<string, unknown>; byPeriod?: Record<string, unknown> }>;
    getCostAnalytics: (params?: { startDate?: string; endDate?: string }) => Promise<AdminCostAnalytics>;
    getRevenueAnalytics: (params?: { startDate?: string; endDate?: string }) => Promise<{ period: { start: string; end: string }; totalRevenueMillicents: number; totalCostMillicents: number; marginMillicents: number; byTier: Record<string, unknown> }>;

    // Audit
    listAuditEvents: (params?: AdminAuditListParams) => Promise<{ events: AdminAuditEvent[]; total: number }>;
    getAuditEvent: (eventId: string) => Promise<AdminAuditEvent>;

    // Agents
    listAgents: () => Promise<{ agents: AdminAgentStatus[] }>;
  };

  // ChatGPT Import
  chatgptImport: {
    saveToken: (accessToken: string) => Promise<{ connected: boolean }>;
    tokenStatus: () => Promise<{ connected: boolean }>;
    fetchConversation: (url: string) => Promise<{ threadId: string; title: string; nodeCount: number; stored?: number; skipped?: number; tokenExpired?: boolean }>;
  };

  // File Import (direct file upload)
  fileImport: {
    /** Upload a single ChatGPT conversation JSON */
    uploadConversation: (file: File) => Promise<{
      success: boolean;
      threadId: string;
      title: string;
      messageCount: number;
      stored: number;
      skipped: number;
    }>;
    /** Upload multiple conversation files */
    uploadConversations: (files: File[]) => Promise<{
      success: boolean;
      total: number;
      imported: number;
      failed: number;
      conversations: Array<{
        title: string;
        threadId: string;
        messageCount: number;
        stored: number;
        skipped: number;
        error?: string;
      }>;
    }>;
    /** Upload a Humanizer export file */
    uploadHumanizerExport: (file: File) => Promise<{
      success: boolean;
      total: number;
      imported: number;
      failed: number;
      messageCount: number;
    }>;
  };

  // Analyze
  analyze: {
    detect: (text: string) => Promise<DetectionResult>;
    detectGptzero: (text: string) => Promise<any>;
    sic: (text: string, genreHint?: string) => Promise<SicResult>;
    sentenceStart: (text: string) => Promise<SentenceSessionInfo>;
    sentenceStep: (sessionId: string) => Promise<SentenceStepResult>;
  };

  // Transform
  transform: {
    humanize: (text: string, intensity?: 'light' | 'moderate' | 'aggressive') => Promise<HumanizationResult>;
    persona: (text: string, personaId: string) => Promise<TransformResult>;
    personaCustom: (text: string, persona: { name: string; systemPrompt: string }) => Promise<TransformResult>;
    style: (text: string, styleId: string) => Promise<TransformResult>;
    styleCustom: (text: string, style: { name: string; stylePrompt: string }) => Promise<TransformResult>;
    listPersonas: () => Promise<{ personas: PresetInfo[] }>;
    listStyles: () => Promise<{ styles: PresetInfo[] }>;
    synthesizeVoice: (samples: string[]) => Promise<{ persona: { name: string; systemPrompt: string; traits?: string[]; toneMarkers?: string[] } }>;
    summarize: (text: string, style?: 'brief' | 'detailed') => Promise<{ summary: string }>;
    embed: (text: string, sourceBufferId?: string, projectId?: string) => Promise<{ chunks: number; embedded: number; contentChunks: string[] }>;
  };

  // Personas (DB-backed CRUD)
  personas: {
    create: (input: PersonaProfileInput) => Promise<{ persona: PresetInfo }>;
    update: (id: string, input: Partial<PersonaProfileInput>) => Promise<{ persona: PresetInfo }>;
    delete: (id: string) => Promise<{ deleted: boolean }>;
    reset: (systemPersonaId: string) => Promise<{ reset: boolean }>;
    shadow: (systemPersonaId: string, overrides?: Partial<PersonaProfileInput>) => Promise<{ persona: PresetInfo }>;
  };

  // Projects (DB-backed)
  projects: {
    list: () => Promise<{ projects: any[] }>;
    get: (id: string) => Promise<{ project: any; bufferIds: string[]; harvests: any[] }>;
    create: (body: { name: string; description?: string; sessionId?: string }) => Promise<{ project: any }>;
    update: (id: string, body: { name?: string; description?: string }) => Promise<{ project: any }>;
    delete: (id: string) => Promise<void>;
    addBuffer: (projectId: string, bufferId: string) => Promise<any>;
    removeBuffer: (projectId: string, bufferId: string) => Promise<any>;
    listBuffers: (projectId: string) => Promise<{ bufferIds: string[] }>;
    listHarvests: (projectId: string) => Promise<{ harvests: any[] }>;
  };

  // Harvests (DB-backed)
  harvests: {
    get: (id: string) => Promise<{ harvest: any; items: any[] }>;
    create: (projectId: string, name: string) => Promise<{ harvest: any }>;
    delete: (id: string) => Promise<void>;
    addItem: (harvestId: string, content: string, source?: string) => Promise<{ item: any }>;
    addItemsBatch: (harvestId: string, items: Array<{ content: string; source?: string }>) => Promise<{ items: any[] }>;
    removeItem: (harvestId: string, itemId: string) => Promise<any>;
    listItems: (harvestId: string) => Promise<any[]>;
    cull: (harvestId: string, threshold?: number) => Promise<{ candidates: any[] }>;
    applyCull: (harvestId: string, actions: Array<{ itemId: string; action: 'keep' | 'cull' }>) => Promise<{ kept: number; culled: number }>;
    generateDraft: (harvestId: string, opts?: { targetWordCount?: number; arcType?: string }) => Promise<{ arc: any; draft: string; cullReport: any; wordCount: number; itemCount: number }>;
  };

  // Workspace State (cross-session persistence)
  workspaceState: {
    get: (key: string) => Promise<{ key: string; value: unknown; updatedAt?: string }>;
    set: (key: string, value: unknown) => Promise<{ key: string; value: unknown; updatedAt: string }>;
    getAll: () => Promise<{ state: Record<string, unknown> }>;
  };

  // Settings (user-facing)
  settings: {
    // Profile
    getProfile: () => Promise<{ userId: string; email: string; role: string }>;
    updateProfile: (params: { displayName?: string; timezone?: string }) => Promise<void>;
    changePassword: (currentPassword: string, newPassword: string) => Promise<void>;

    // API Keys
    listApiKeys: () => Promise<{ keys: SettingsApiKey[]; count: number }>;
    createApiKey: (params: { name: string; scopes?: string[]; expiresIn?: string }) => Promise<{ key: string; id: string; keyPrefix: string }>;
    revokeApiKey: (keyId: string) => Promise<void>;

    // Usage
    getUsage: () => Promise<SettingsUsageSummary>;
    getUsageHistory: (periods?: number) => Promise<{ history: SettingsUsageHistory[] }>;

    // Preferences
    getPreferences: () => Promise<{ preferences: SettingsPreferences }>;
    updatePreferences: (params: Partial<SettingsPreferences>) => Promise<void>;

    // Custom Prompts (PRO+)
    listCustomPrompts: () => Promise<{ prompts: SettingsCustomPrompt[]; limit: number }>;
    createCustomPrompt: (params: { name: string; description?: string; template: string }) => Promise<SettingsCustomPrompt>;
    updateCustomPrompt: (promptId: string, params: { name?: string; description?: string; template?: string }) => Promise<SettingsCustomPrompt>;
    deleteCustomPrompt: (promptId: string) => Promise<void>;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const ApiContext = createContext<ApiClient | null>(null);

export function useApi(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error('useApi must be used within ApiProvider');
  }
  return ctx;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

interface ApiProviderProps {
  baseUrl?: string;
  children: React.ReactNode;
}

/**
 * Determine API base URL based on environment
 * Priority: 1) explicit prop, 2) VITE_API_URL, 3) auto-detect from hostname/platform
 */
function getDefaultApiUrl(): string {
  // Check for explicit env variable first
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Auto-detect production based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Cloudflare Pages deployments
  if (hostname.includes('.pages.dev') || hostname.includes('humanizer.com')) {
    return 'https://humanizer-cloud.tem-527.workers.dev';
  }

  // Electron app: use a no-op URL so accidental cloud calls fail fast.
  // All real Electron data flows through IPC (window.electronArchive, etc.),
  // NOT through the HTTP API client. Any fetch to this URL is a bug.
  if (typeof window !== 'undefined' && window.isElectron) {
    return 'http://localhost:0/electron-no-cloud';
  }

  // Local development default (non-Electron web dev)
  return 'http://localhost:3030';
}

export function ApiProvider({ baseUrl = getDefaultApiUrl(), children }: ApiProviderProps) {
  const { getAuthHeaders } = useAuth();

  // Create a ky instance with token-based auth
  const client: KyInstance = useMemo(
    () =>
      ky.create({
        prefixUrl: baseUrl,
        headers: {
          'Content-Type': 'application/json',
        },
        hooks: {
          beforeRequest: [
            (request) => {
              // Add auth headers (Bearer token + tenant ID)
              const authHeaders = getAuthHeaders();
              for (const [key, value] of Object.entries(authHeaders)) {
                if (key !== 'Content-Type') { // Don't override Content-Type
                  request.headers.set(key, value);
                }
              }
            },
          ],
          beforeError: [
            async (error) => {
              const { response } = error;
              if (response?.status === 429) {
                const retryAfter = response.headers.get('Retry-After');
                const waitMsg = retryAfter
                  ? `Please wait ${retryAfter} seconds and try again.`
                  : 'Please wait a moment and try again.';
                error.message = `Rate limit reached — too many requests. ${waitMsg}`;
                error.name = 'RateLimitError';
              }
              return error;
            },
          ],
        },
      }),
    [baseUrl, getAuthHeaders]
  );

  const api: ApiClient = useMemo(
    () => ({
      // Sessions
      listSessions: () => client.get('sessions').json(),
      createSession: (options) => client.post('sessions', { json: options }).json(),
      getSession: (id) => client.get(`sessions/${id}`).json(),
      deleteSession: (id) => client.delete(`sessions/${id}`).json(),

      // Chat (LLM agent) — longer timeout for multi-step agentic loops
      chat: (sessionId, message, history) =>
        client
          .post(`sessions/${sessionId}/chat`, {
            json: {
              message,
              history: history?.map((m) => ({ role: m.role, content: m.content })),
            },
            timeout: 120_000,
          })
          .json(),

      // Buffers
      listBuffers: (sessionId) => client.get(`sessions/${sessionId}/buffers`).json(),
      createBuffer: (sessionId, name, content) =>
        client.post(`sessions/${sessionId}/buffers`, { json: { name, content } }).json(),
      getBuffer: (sessionId, name) => client.get(`sessions/${sessionId}/buffers/${name}`).json(),
      setBufferContent: async (sessionId, name, content) => {
        await client.put(`sessions/${sessionId}/buffers/${name}/content`, { json: { content } });
      },
      appendToBuffer: async (sessionId, name, items) => {
        await client.post(`sessions/${sessionId}/buffers/${name}/append`, { json: { items } });
      },
      commitBuffer: (sessionId, name, message) =>
        client.post(`sessions/${sessionId}/buffers/${name}/commit`, { json: { message } }).json(),
      getBufferHistory: (sessionId, name, limit) =>
        client.get(`sessions/${sessionId}/buffers/${name}/history`, { searchParams: { limit: limit ?? 10 } }).json(),
      deleteBuffer: (sessionId, name, force) =>
        client.delete(`sessions/${sessionId}/buffers/${name}`, { searchParams: force ? { force: 'true' } : {} }).json(),
      branchBuffer: (sessionId, name, branchName) =>
        client.post(`sessions/${sessionId}/buffers/${name}/branch`, { json: { branchName } }).json(),
      switchBranch: (sessionId, name, branchName) =>
        client.post(`sessions/${sessionId}/buffers/${name}/switch`, { json: { branchName } }).json(),
      rollbackBuffer: (sessionId, name, steps) =>
        client.post(`sessions/${sessionId}/buffers/${name}/rollback`, { json: { steps: steps ?? 1 } }).json(),
      pinBuffer: (sessionId, name, pinned) =>
        client.patch(`sessions/${sessionId}/buffers/${name}/pin`, { json: { pinned } }).json(),

      // User-scoped Buffers
      userBuffers: {
        list: (type) =>
          client.get('users/me/buffers', { searchParams: type ? { type } : {} }).json(),
        create: (body) =>
          client.post('users/me/buffers', { json: body }).json(),
        getVoice: () =>
          client.get('users/me/buffers/voice-buffer').json(),
        updateContent: async (name, content) => {
          await client.put(`users/me/buffers/${name}/content`, { json: { content } });
        },
        listAll: (sessionId?: string) =>
          client.get('users/me/buffers/all', { searchParams: sessionId ? { sessionId } : {} }).json(),
      },

      // Search
      search: (sessionId, query, options) =>
        client.post('search', { json: { sessionId, query, ...options } }).json(),
      findSimilar: (sessionId, text, options) =>
        client.post('search/similar', { json: { sessionId, text, ...options } }).json(),
      addAnchor: (sessionId, resultId, type) =>
        client.post('search/anchor', { json: { sessionId, resultId, type } }).json(),

      // Archive (UCG browsing)
      archive: {
        browse: (options) =>
          client.get('archive/browse', { searchParams: options as Record<string, string | number> ?? {} }).json(),
        getSources: () => client.get('archive/sources').json(),
        getThreads: (options) =>
          client.get('archive/threads', { searchParams: options as Record<string, string | number> ?? {} }).json(),
        getThread: (threadId) => client.get(`archive/thread/${threadId}`).json(),
        getNode: (nodeId) => client.get(`archive/node/${nodeId}`).json(),
        getStats: () => client.get('archive/stats').json(),
      },

      // Clusters
      listClusters: (options) => client.get('clusters', { searchParams: options }).json(),
      getCluster: (clusterId) => client.get(`clusters/${clusterId}`).json(),
      discoverClusters: (options) => client.post('clusters/discover', { json: options }).json(),
      generateClusterDraft: (clusterId, opts) =>
        client.post(`clusters/${clusterId}/generate-draft`, { json: opts ?? {}, timeout: 300_000 }).json(),

      // Books
      listBooks: (options) => client.get('books', { searchParams: options }).json(),
      createBookFromCluster: (clusterId, options) =>
        client.post('books/from-cluster', { json: { clusterId, ...options } }).json(),
      exportBook: (bookId, format) =>
        client.get(`books/${bookId}/export`, { searchParams: { format: format ?? 'markdown' } }).text(),

      // Admin
      admin: {
        // Status
        getStatus: () => client.get('admin/status').json(),

        // Users
        listUsers: (params) => client.get('admin/users', { searchParams: params as Record<string, string | number> }).json(),
        getUser: (userId) => client.get(`admin/users/${userId}`).json(),
        createUser: (params) => client.post('admin/users', { json: params }).json(),
        updateUser: (userId, params) => client.put(`admin/users/${userId}`, { json: params }).json(),
        updateUserRole: async (userId, role, reason) => {
          await client.put(`admin/users/${userId}/role`, { json: { role, reason } });
        },
        banUser: async (userId, reason, duration) => {
          await client.post(`admin/users/${userId}/ban`, { json: { reason, duration } });
        },
        unbanUser: async (userId) => {
          await client.post(`admin/users/${userId}/unban`);
        },
        deleteUser: async (userId) => {
          await client.delete(`admin/users/${userId}`);
        },

        // Tiers
        listTiers: () => client.get('admin/tiers').json(),
        updateTier: async (tierId, params) => {
          await client.put(`admin/tiers/${tierId}`, { json: params });
        },

        // Pricing
        listPricing: () => client.get('admin/pricing').json(),
        updatePricing: async (key, value) => {
          await client.put(`admin/pricing/${encodeURIComponent(key)}`, { json: { value } });
        },
        listProviderCosts: () => client.get('admin/provider-costs').json(),

        // API Keys
        listApiKeys: (params) => client.get('admin/api-keys', { searchParams: params as Record<string, string | number> ?? {} }).json(),
        revokeApiKey: async (keyId, reason) => {
          await client.delete(`admin/api-keys/${keyId}`, { json: { reason: reason ?? 'Admin revocation' } });
        },

        // Models
        listModels: () => client.get('admin/models').json(),
        updateModel: async (modelId, params) => {
          await client.put(`admin/models/${modelId}`, { json: params });
        },

        // Prompts
        listPrompts: () => client.get('admin/prompts').json(),
        getPrompt: (id) => client.get(`admin/prompts/${id}`).json<{ prompt: AdminPrompt }>().then(r => r.prompt),
        updatePrompt: async (id, params) => {
          await client.put(`admin/prompts/${id}`, { json: params });
        },

        // Providers
        listProviders: () => client.get('admin/providers').json(),
        updateProvider: async (providerId, params) => {
          await client.put(`admin/providers/${providerId}`, { json: params });
        },
        checkProviderHealth: (providerId) => client.post(`admin/providers/${providerId}/health`).json(),
        validateProviderKey: (providerId, apiKey) =>
          client.post(`admin/providers/${providerId}/validate-key`, { json: { apiKey } }).json(),
        removeProviderApiKey: (providerId) =>
          client.delete(`admin/providers/${providerId}/api-key`).json(),
        discoverOllamaModels: () =>
          client.post('admin/providers/ollama/discover').json(),

        // Features
        listFeatures: () => client.get('admin/features').json(),
        updateFeature: async (featureId, params) => {
          await client.put(`admin/features/${featureId}`, { json: params });
        },

        // Subscriptions
        listSubscriptions: (params) => client.get('admin/subscriptions', { searchParams: params as Record<string, string | number> ?? {} }).json(),
        getSubscription: (subscriptionId) => client.get(`admin/subscriptions/${subscriptionId}`).json(),

        // Analytics
        getUsage: (params) => client.get('admin/analytics/usage', { searchParams: params as Record<string, string | number> ?? {} }).json(),
        getCostAnalytics: (params) => client.get('admin/analytics/costs', { searchParams: params as Record<string, string | number> ?? {} }).json(),
        getRevenueAnalytics: (params) => client.get('admin/analytics/revenue', { searchParams: params as Record<string, string | number> ?? {} }).json(),

        // Audit
        listAuditEvents: (params) => client.get('admin/audit', { searchParams: params as Record<string, string | number> ?? {} }).json(),
        getAuditEvent: (eventId) => client.get(`admin/audit/${eventId}`).json(),

        // Agents
        listAgents: () => client.get('admin/agents').json(),
      },

      // ChatGPT Import
      chatgptImport: {
        saveToken: (accessToken) =>
          client.post('chatgpt-import/token', { json: { accessToken } }).json(),
        tokenStatus: () =>
          client.get('chatgpt-import/token/status').json(),
        fetchConversation: (url) =>
          client.post('chatgpt-import/fetch', { json: { url }, timeout: 30_000 }).json(),
      },

      // File Import (direct file upload)
      // Note: Set Content-Type to undefined to let browser set multipart/form-data with boundary
      fileImport: {
        uploadConversation: (file: File) => {
          const form = new FormData();
          form.append('file', file);
          return client.post('import/conversation', { body: form, headers: { 'Content-Type': undefined } }).json();
        },
        uploadConversations: (files: File[]) => {
          const form = new FormData();
          for (const file of files) {
            form.append('files', file);
          }
          return client.post('import/conversations', { body: form, headers: { 'Content-Type': undefined } }).json();
        },
        uploadHumanizerExport: (file: File) => {
          const form = new FormData();
          form.append('file', file);
          return client.post('import/humanizer-export', { body: form, headers: { 'Content-Type': undefined } }).json();
        },
      },

      // Analyze — LLM endpoints need longer timeout
      analyze: {
        detect: (text) => client.post('analyze/detect', { json: { text } }).json(),
        detectGptzero: (text) => client.post('analyze/gptzero', { json: { text }, timeout: 30_000 }).json(),
        sic: (text, genreHint) => client.post('analyze/sic', { json: { text, genreHint }, timeout: 120_000 }).json(),
        sentenceStart: (text) => client.post('analyze/sentence/start', { json: { text }, timeout: 120_000 }).json(),
        sentenceStep: (sessionId) => client.post('analyze/sentence/step', { json: { sessionId }, timeout: 120_000 }).json(),
      },

      // Transform — LLM-based, need longer timeout
      transform: {
        humanize: (text, intensity) => client.post('transform/humanize', { json: { text, intensity }, timeout: 120_000 }).json(),
        persona: (text, personaId) => client.post('transform/persona', { json: { text, personaId }, timeout: 120_000 }).json(),
        personaCustom: (text, persona) => client.post('transform/persona', { json: { text, customPersona: persona }, timeout: 120_000 }).json(),
        style: (text, styleId) => client.post('transform/style', { json: { text, styleId }, timeout: 120_000 }).json(),
        styleCustom: (text, style) => client.post('transform/style', { json: { text, customStyle: style }, timeout: 120_000 }).json(),
        listPersonas: () => client.get('transform/personas').json(),
        listStyles: () => client.get('transform/styles').json(),
        synthesizeVoice: (samples) => client.post('transform/synthesize-voice', { json: { samples }, timeout: 120_000 }).json(),
        summarize: (text: string, style?: 'brief' | 'detailed') =>
          client.post('transform/summarize', { json: { text, style }, timeout: 120_000 }).json<{ summary: string }>(),
        embed: (text: string, sourceBufferId?: string, projectId?: string) =>
          client.post('transform/embed', { json: { text, sourceBufferId, projectId } }).json<{ chunks: number; embedded: number; contentChunks: string[] }>(),
      },

      // Media
      media: {
        list: (options) =>
          client.get('media', { searchParams: options as Record<string, string | number> ?? {} }).json(),
        byPointer: (pointer) =>
          client.get('media/by-pointer', { searchParams: { pointer } }).json(),
        search: (query, limit) =>
          client.get('media/search', { searchParams: { q: query, ...(limit ? { limit } : {}) } }).json(),
        upload: (file, options) => {
          const form = new FormData();
          form.append('file', file);
          if (options?.sourceType) form.append('sourceType', options.sourceType);
          if (options?.caption) form.append('caption', options.caption);
          if (options?.nodeId) form.append('nodeId', options.nodeId);
          return client.post('media/upload', { body: form, headers: { 'Content-Type': undefined } }).json();
        },
        transcribe: (mediaId, type, archiveId) =>
          client.post('media/transcribe', { json: { mediaId, type, archiveId }, timeout: 120_000 }).json(),
        getTranscriptions: (mediaId, archiveId) =>
          client.get(`media/${mediaId}/transcriptions`, { searchParams: archiveId ? { archiveId } : {} }).json(),
        setPreferredTranscription: (versionId) =>
          client.post(`media/transcriptions/${versionId}/prefer`).json(),
      },

      // Overrides (user content edits)
      overrides: {
        save: (nodeId, originalText, editedText, editReason) =>
          client.post('overrides', { json: { nodeId, originalText, editedText, editReason } }).json(),
        get: (nodeId) => client.get(`overrides/${nodeId}`).json(),
        getBatch: (nodeIds) =>
          client.get(`overrides/batch?nodeIds=${nodeIds.join(',')}`).json(),
        delete: async (nodeId) => {
          await client.delete(`overrides/${nodeId}`);
          return { deleted: true };
        },
      },

      // Personas (DB-backed CRUD)
      personas: {
        create: (input: PersonaProfileInput) =>
          client.post('transform/personas', { json: input }).json<{ persona: PresetInfo }>(),
        update: (id: string, input: Partial<PersonaProfileInput>) =>
          client.put(`transform/personas/${id}`, { json: input }).json<{ persona: PresetInfo }>(),
        delete: async (id: string) => {
          await client.delete(`transform/personas/${id}`);
          return { deleted: true };
        },
        reset: (systemPersonaId: string) =>
          client.post(`transform/personas/${systemPersonaId}/reset`).json<{ reset: boolean }>(),
        shadow: (systemPersonaId: string, overrides?: Partial<PersonaProfileInput>) =>
          client.post(`transform/personas/${systemPersonaId}/shadow`, { json: overrides ?? {} }).json<{ persona: PresetInfo }>(),
      },

      // Projects (DB-backed)
      projects: {
        list: () => client.get('projects').json<{ projects: any[] }>(),
        get: (id: string) => client.get(`projects/${id}`).json<{ project: any; bufferIds: string[]; harvests: any[] }>(),
        create: (body: { name: string; description?: string; sessionId?: string }) =>
          client.post('projects', { json: body }).json<{ project: any }>(),
        update: (id: string, body: { name?: string; description?: string }) =>
          client.put(`projects/${id}`, { json: body }).json<{ project: any }>(),
        delete: async (id: string) => { await client.delete(`projects/${id}`); },
        addBuffer: (projectId: string, bufferId: string) =>
          client.post(`projects/${projectId}/buffers`, { json: { bufferId } }).json(),
        removeBuffer: (projectId: string, bufferId: string) =>
          client.delete(`projects/${projectId}/buffers/${bufferId}`).json(),
        listBuffers: (projectId: string) =>
          client.get(`projects/${projectId}/buffers`).json<{ bufferIds: string[] }>(),
        listHarvests: (projectId: string) =>
          client.get(`projects/${projectId}/harvests`).json<{ harvests: any[] }>(),
      },

      // Harvests (DB-backed)
      harvests: {
        get: (id: string) => client.get(`harvests/${id}`).json<{ harvest: any; items: any[] }>(),
        create: (projectId: string, name: string) =>
          client.post(`projects/${projectId}/harvests`, { json: { name } }).json<{ harvest: any }>(),
        delete: async (id: string) => { await client.delete(`harvests/${id}`); },
        addItem: (harvestId: string, content: string, source?: string) =>
          client.post(`harvests/${harvestId}/items`, { json: { content, source } }).json<{ item: any }>(),
        addItemsBatch: (harvestId: string, items: Array<{ content: string; source?: string }>) =>
          client.post(`harvests/${harvestId}/items/batch`, { json: { items } }).json<{ items: any[] }>(),
        removeItem: (harvestId: string, itemId: string) =>
          client.delete(`harvests/${harvestId}/items/${itemId}`).json(),
        listItems: (harvestId: string) =>
          client.get(`harvests/${harvestId}`).json<{ harvest: any; items: any[] }>().then(r => r.items),
        cull: (harvestId: string, threshold?: number) =>
          client.post(`harvests/${harvestId}/cull`, { json: { threshold }, timeout: 120_000 }).json<{ candidates: any[] }>(),
        applyCull: (harvestId: string, actions: Array<{ itemId: string; action: 'keep' | 'cull' }>) =>
          client.post(`harvests/${harvestId}/cull/apply`, { json: { actions } }).json<{ kept: number; culled: number }>(),
        generateDraft: (harvestId: string, opts?: { targetWordCount?: number; arcType?: string }) =>
          client.post(`harvests/${harvestId}/generate-draft`, { json: opts ?? {}, timeout: 300_000 }).json<{ arc: any; draft: string; cullReport: any; wordCount: number; itemCount: number }>(),
      },

      // Workspace State (cross-session persistence)
      workspaceState: {
        get: (key) => client.get(`users/me/workspace-state/${key}`).json(),
        set: (key, value) => client.put(`users/me/workspace-state/${key}`, { json: { value } }).json(),
        getAll: () => client.get('users/me/workspace-state').json(),
      },

      // Settings (user-facing)
      settings: {
        // Profile
        getProfile: () => client.get('settings/profile').json(),
        updateProfile: async (params) => {
          await client.put('settings/profile', { json: params });
        },
        changePassword: async (currentPassword, newPassword) => {
          await client.put('settings/password', { json: { currentPassword, newPassword } });
        },

        // API Keys
        listApiKeys: () => client.get('settings/api-keys').json(),
        createApiKey: (params) => client.post('settings/api-keys', { json: params }).json(),
        revokeApiKey: async (keyId) => {
          await client.delete(`settings/api-keys/${keyId}`);
        },

        // Usage
        getUsage: () => client.get('settings/usage').json(),
        getUsageHistory: (periods = 6) => client.get('settings/usage/history', { searchParams: { periods } }).json(),

        // Preferences
        getPreferences: () => client.get('settings/preferences').json(),
        updatePreferences: async (params) => {
          await client.put('settings/preferences', { json: params });
        },

        // Custom Prompts (PRO+)
        listCustomPrompts: () => client.get('settings/prompts').json(),
        createCustomPrompt: (params) => client.post('settings/prompts', { json: params }).json<{ prompt: SettingsCustomPrompt }>().then(r => r.prompt),
        updateCustomPrompt: (promptId, params) => client.put(`settings/prompts/${promptId}`, { json: params }).json<{ prompt: SettingsCustomPrompt }>().then(r => r.prompt),
        deleteCustomPrompt: async (promptId) => {
          await client.delete(`settings/prompts/${promptId}`);
        },
      },
    }),
    [client]
  );

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}
